/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import TagBadge from '../../components/TagBadge';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';

export default function Customers() {
  const routeName = '/customers';
  const dataName = 'Cliente';
  const titleName = 'Clientes';
  const navigate = useNavigate();
  const notification = useNotification();

  const [apiErrors, setApiErrors] = useState({});
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- ESTADOS DE PAGINAÇÃO E BUSCA ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // --- ESTADOS PARA FILTRO POR ETIQUETAS ---
  const [selectedTagId, setSelectedTagId] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedActive, setSelectedActive] = useState('');

  // Debounce para evitar requisições a cada tecla pressionada
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset para página 1 quando mudar filtro de tag
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTagId, selectedActive]);

  const tableColumns = useMemo(
    () => [
      { Header: 'Nome', accessor: 'nomeEmpresa' },
      { Header: 'Razão Social', accessor: 'razaoSocial' },
      { Header: 'CPF / CNPJ', accessor: 'cpfCnpj' },
      {
        Header: 'Etiquetas',
        accessor: 'tags',
        noSearch: true,
        disableSortBy: true,
      },
      { Header: 'Status', accessor: 'status' },
      {
        Header: 'Ações',
        accessor: 'actions',
        noSearch: true,
        disableSortBy: true,
      },
    ],
    []
  );

  // Carregar etiquetas disponíveis
  const loadAvailableTags = useCallback(async () => {
    try {
      setTagsLoading(true);
      const response = await axios.get('/tags');
      setAvailableTags(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar etiquetas:', err);
      setAvailableTags([]);
    } finally {
      setTagsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableTags();
  }, [loadAvailableTags]);

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        q: debouncedSearchText,
        page: currentPage,
        limit,
      };

      // Adicionar filtro por tag se selecionado
      if (selectedTagId) {
        params.tagId = selectedTagId;
      }

      if (selectedActive) {
        params.active = selectedActive === 'active' ? 'true' : 'false';
      }

      const response = await axios.get(routeName, { params });
      setData(response.data.customers);
      setTotalCount(response.data.totalCount);
      setTotalPages(Math.ceil(response.data.totalCount / limit));
    } catch (err) {
      setApiErrors(err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    routeName,
    debouncedSearchText,
    currentPage,
    limit,
    selectedTagId,
    selectedActive,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  const confirmDelete = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        await axios.delete(`${routeName}/${id}`);
        notification(`${dataName} removido com sucesso`, 'success');
        getData();
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [notification, getData]
  );

  const tableData = useMemo(
    () =>
      data.map((item) => ({
        nomeEmpresa: (
          <div
            style={{ cursor: 'pointer', color: '#007bff' }}
            onClick={() => navigate(`${routeName}/${item.id}/edit`)}
            title="Clique para editar"
          >
            {item.nomeEmpresa}
          </div>
        ),
        razaoSocial: (
          <div
            style={{ cursor: 'pointer', color: '#007bff' }}
            onClick={() => navigate(`${routeName}/${item.id}/edit`)}
            title="Clique para editar"
          >
            {item.razaoSocial}
          </div>
        ),
        cpfCnpj: item.cpfCnpj,
        tags: (
          <div className="d-flex flex-wrap gap-1">
            {item.tags && item.tags.length > 0 ? (
              item.tags.map((tag) => (
                <TagBadge key={tag.id} tag={tag} showRemove={false} />
              ))
            ) : (
              <span className="text-muted">Sem etiquetas</span>
            )}
          </div>
        ),
        status: (
          <span className={item.active ? 'text-success' : 'text-muted'}>
            {item.active ? 'Ativo' : 'Inativo'}
          </span>
        ),
        actions: (
          <div
            className="d-flex justify-content-end gap-1"
            style={{ display: 'flex', gap: '0.5rem' }}
          >
            <Button
              variant={item.active ? 'outline-secondary' : 'outline-success'}
              size="sm"
              onClick={async () => {
                try {
                  setIsLoading(true);
                  await axios.put(`${routeName}/${item.id}`, {
                    active: !item.active,
                  });
                  notification(
                    `Cliente ${item.active ? 'inativado' : 'ativado'} com sucesso`,
                    'success'
                  );
                  getData();
                } catch (err) {
                  setApiErrors(err);
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              {item.active ? 'Inativar' : 'Ativar'}
            </Button>
            <ButtonModal
              onConfirm={() => confirmDelete(item.id)}
              message={`Confirma remoção do ${dataName} ${item.razaoSocial}?`}
              buttonProps={{
                variant: 'danger',
                size: 'sm',
                faicon: 'faTrash',
              }}
              textModal={{
                title: `Remover ${dataName}`,
                textConfirm: 'Remover',
              }}
            >
              <FaTrash />
            </ButtonModal>
          </div>
        ),
      })),
    [data, navigate, confirmDelete, notification, getData]
  );

  // --- LÓGICA DA NOVA PAGINAÇÃO INTELIGENTE ---
  const renderPaginationItems = () => {
    const pages = [];
    const maxPagesToShow = 5; // Número máximo de páginas a serem exibidas na barra
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (startPage > 1) {
      pages.push(
        <Pagination.First key="first" onClick={() => setCurrentPage(1)} />
      );
      pages.push(
        <Pagination.Prev
          key="prev"
          onClick={() => setCurrentPage(currentPage - 1)}
        />
      );
      pages.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    } else {
      pages.push(
        <Pagination.Prev
          key="prev"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        />
      );
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      pages.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      pages.push(
        <Pagination.Next
          key="next"
          onClick={() => setCurrentPage(currentPage + 1)}
        />
      );
      pages.push(
        <Pagination.Last
          key="last"
          onClick={() => setCurrentPage(totalPages)}
        />
      );
    } else {
      pages.push(
        <Pagination.Next
          key="next"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
      );
    }

    return pages;
  };

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title={titleName} />

      <Row className="mb-3 align-items-center">
        <Col>
          <h1 className="page-heading">{titleName}</h1>
        </Col>
        <Col xs="auto">
          <Link to={`${routeName}/new`}>
            <Button variant="primary" className="text-uppercase">
              <FaPlus /> Cadastrar
            </Button>
          </Link>
        </Col>
      </Row>

      {/* Filtros de busca e etiquetas */}
      <Row className="mb-3 align-items-end">
        <Col md={4}>
          <Form.Group controlId="search">
            <Form.Label>Buscar clientes</Form.Label>
            <Form.Control
              type="search"
              placeholder="Pesquisar por nome, razão social ou CPF/CNPJ..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="tagFilter">
            <Form.Label>
              Filtrar por etiqueta
              {selectedTagId && (
                <span className="text-primary ms-2">(filtro ativo)</span>
              )}
            </Form.Label>
            <Form.Select
              value={selectedTagId}
              onChange={(e) => setSelectedTagId(e.target.value)}
              disabled={tagsLoading}
            >
              <option value="">Todas as etiquetas</option>
              {availableTags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="activeFilter">
            <Form.Label>
              Status
              {selectedActive && (
                <span className="text-primary ms-2">(filtro ativo)</span>
              )}
            </Form.Label>
            <Form.Select
              value={selectedActive}
              onChange={(e) => setSelectedActive(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </Form.Select>
          </Form.Group>
        </Col>
        {selectedTagId && (
          <Col md={4}>
            <Button
              variant="outline-secondary"
              onClick={() => setSelectedTagId('')}
              className="mt-2"
            >
              Limpar filtro
            </Button>
          </Col>
        )}
      </Row>

      <Card className="card-table mb-5">
        <ErrorProcessor error={apiErrors} />
        <DataTable
          items={tableData}
          columns={tableColumns}
          defaultPageSize={limit}
        />
      </Card>

      {/* --- PAGINAÇÃO INTELIGENTE E CONTADOR --- */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          Mostrando {data.length} de {totalCount} clientes
          {selectedTagId && (
            <span className="text-muted ms-2">(filtrados por etiqueta)</span>
          )}
        </div>
        <Pagination>{renderPaginationItems()}</Pagination>
      </div>
    </Container>
  );
}
